// Kitchen / line / CIP busy intervals for the Resource usage hourly table,
// derived straight from schedule rows.
//
// The hourly table used to rely on the host passing ready-built Gantt
// resources. `FactoryScheduleView` passes none, so on that host the table
// showed only the Resources group while the heat map above it showed
// Kitchens, Lines and CIP stations. Deriving from the rows the view already
// receives gives every host the same groups. The field reading mirrors
// `ScheduleFullGantt`'s kitchen/line/CIP lanes so both agree hour for hour.

import { InputRow } from 'Features/SchedulerView/SchedulerView.types'
import { rebaseRowsToWeek, WEEK_HOURS } from 'Features/SchedulerView/ClipToWeek'
import { GanttResourceData } from './Scheduler.types'

export type Interval = { start: number; end: number }

/** The shape `buildAssetHourlyRow` reads. `GanttResourceData` satisfies it
 *  structurally, so Gantt-built resources and row-derived ones mix freely. */
export type AssetIntervals = {
  name: string
  productionBars: Interval[]
  cleaningBars: Interval[]
  downtimeIntervals: Interval[]
}

type CipEvent = { start: number; end: number; cip?: string }

const parseCipEvents = (raw: string | null | undefined): CipEvent[] => {
  if (!raw) return []
  try {
    // Wire format is postprocess.rs::cip_events_to_json — `start`/`end`,
    // NOT `start_time`/`end_time`.
    const parsed = JSON.parse(raw) as CipEvent[]
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

const isNum = (v: unknown): v is number =>
  typeof v === 'number' && Number.isFinite(v)

type Side = {
  asset: string | undefined
  prod: [unknown, unknown]
  clean: [unknown, unknown, string | undefined]
  preclean: [unknown, unknown, string | undefined]
  additional: string | null | undefined
}

/**
 * Kitchens, lines and CIP stations as busy intervals, sorted by name.
 *
 * Exact duplicate intervals are dropped per asset: one kitchen batch feeding
 * two lines is two output rows with the same kitchen window, and summing both
 * would read as 200% busy (the engine's own `unique_kitchen_runs` dedups for
 * the same reason). No downtime is available from rows, so assets derived
 * here are measured against a full hour every hour.
 */
export function deriveAssetIntervals(rows: InputRow[]): {
  kitchens: AssetIntervals[]
  lines: AssetIntervals[]
  cips: AssetIntervals[]
} {
  const kitchenMap = new Map<string, AssetIntervals>()
  const lineMap = new Map<string, AssetIntervals>()
  const cipMap = new Map<string, AssetIntervals>()
  const seen = new Set<string>()

  const asset = (map: Map<string, AssetIntervals>, name: string) => {
    let a = map.get(name)
    if (!a) {
      a = { name, productionBars: [], cleaningBars: [], downtimeIntervals: [] }
      map.set(name, a)
    }
    return a
  }
  // Pushes once per (bucket, owner, window); `owner` lets a CIP keep two
  // different assets' cleans at the same time apart.
  const push = (
    list: Interval[],
    key: string,
    start: unknown,
    end: unknown
  ) => {
    if (!isNum(start) || !isNum(end) || end <= start) return
    const k = `${key}|${start}|${end}`
    if (seen.has(k)) return
    seen.add(k)
    list.push({ start, end })
  }

  const addSide = (map: Map<string, AssetIntervals>, kind: string, s: Side) => {
    if (!s.asset) return
    const a = asset(map, s.asset)
    push(a.productionBars, `${kind}P/${s.asset}`, s.prod[0], s.prod[1])
    const cleans: CipEvent[] = []
    for (const [start, end, cip] of [s.clean, s.preclean]) {
      if (isNum(start) && isNum(end)) cleans.push({ start, end, cip })
    }
    cleans.push(...parseCipEvents(s.additional))
    for (const c of cleans) {
      push(a.cleaningBars, `${kind}C/${s.asset}`, c.start, c.end)
      if (c.cip) {
        push(
          asset(cipMap, c.cip).cleaningBars,
          `cip/${c.cip}/${s.asset}`,
          c.start,
          c.end
        )
      }
    }
  }

  for (const r of rows) {
    addSide(kitchenMap, 'k', {
      asset: r.kitchen,
      prod: [r.kitchen_start_time, r.kitchen_end_time],
      clean: [
        r.kitchen_clean_start_time,
        r.kitchen_clean_end_time,
        r.kitchenCIP,
      ],
      preclean: [
        r.kitchen_preclean_start_time,
        r.kitchen_preclean_end_time,
        r.kitchenPrecleanCIP,
      ],
      additional: r.additional_kitchen_cips,
    })
    addSide(lineMap, 'l', {
      asset: r.line,
      prod: [r.line_start_time, r.line_end_time],
      clean: [r.line_clean_start_time, r.line_clean_end_time, r.lineCIP],
      preclean: [
        r.line_preclean_start_time,
        r.line_preclean_end_time,
        r.linePrecleanCIP,
      ],
      additional: r.additional_line_cips,
    })
  }

  const sorted = (m: Map<string, AssetIntervals>) =>
    Array.from(m.values()).sort((a, b) => a.name.localeCompare(b.name))
  return {
    kitchens: sorted(kitchenMap),
    lines: sorted(lineMap),
    cips: sorted(cipMap),
  }
}

/**
 * Put `rows` on the same 0-168 grid as `kitchenResources`.
 *
 * `ScheduleFullGantt` hands this view its ABSOLUTE rows alongside kitchen
 * resources it has already shifted into the selected week, without saying by
 * how much. A row-derived lane drawn from the absolute rows would then sit a
 * week (or more) off the Gantt-built ones. The shift is recovered from any
 * kitchen bar that names its source row. `FactoryScheduleView` passes rows
 * that are already rebased and no resources, so this is a no-op there.
 */
export function alignRowsToResources(
  rows: InputRow[],
  kitchenResources: GanttResourceData[]
): InputRow[] {
  if (rows.length === 0 || kitchenResources.length === 0) return rows
  const startById = new Map<number, number>()
  for (const r of rows) {
    if (isNum(r.id) && isNum(r.kitchen_start_time)) {
      startById.set(r.id, r.kitchen_start_time)
    }
  }
  for (const res of kitchenResources) {
    for (const bar of res.productionBars) {
      if (bar.rowId == null) continue
      const abs = startById.get(bar.rowId)
      if (abs === undefined) continue
      const offset = Math.round(abs - bar.start)
      // Only whole weeks are a rebase; anything else is a coincidence of ids.
      return offset > 0 && offset % WEEK_HOURS === 0
        ? rebaseRowsToWeek(rows, offset)
        : rows
    }
  }
  return rows
}
